import React from 'react';
import { ArrowLeft, Plus, Smartphone, Trash2 } from 'lucide-react';
import ChannelBadge from '../Chat/ChannelBadge.tsx';
import type { OfficialWhatsAppConfig } from '../../services/api.ts';
import type { WhatsAppDeviceData, WhatsAppProvider, WhatsAppStatusData } from './WhatsAppConnectUI.tsx';

export type ConnectionKind = 'official' | 'device';
export type ConnectionLoadState = 'loading' | 'ready' | 'error';
export type ConnectionView = 'list' | 'add' | 'official' | 'device' | 'pairing' | 'recover';
export interface ConnectionRow {
  kind: ConnectionKind;
  provider?: WhatsAppProvider;
  name: string;
  profileName?: string;
  imageUrl?: string;
  phone: string;
  status: string;
  tone: 'success' | 'error' | 'neutral';
  reconnect: boolean;
}

export const connectionTheme = (dark: boolean) => ({
  canvas: dark ? 'bg-[#071225] text-[#F8FAFC]' : 'bg-[#F7F9FC] text-[#121B29]',
  surface: dark ? 'bg-[#0F1D31] border-[#27364D]' : 'bg-white border-[#E4E8EF]',
  muted: dark ? 'text-[#98A2B3]' : 'text-[#667085]',
  body: dark ? 'text-[#D0D5DD]' : 'text-[#344054]',
  control: dark ? 'bg-[#14243A] hover:bg-[#172A43]' : 'bg-[#F5F8FF] hover:bg-[#E4E8EF]',
  divider: dark ? 'border-[#27364D]' : 'border-[#E4E8EF]',
});

export function hasOfficialConnection(config: OfficialWhatsAppConfig | null) {
  return Boolean(config?.has_official || (config?.status && !['not_connected', 'disconnected'].includes(config.status)));
}

export function getConnectionRows({ official, officialLoad, provider, deviceLoad, device, status }: {
  official: OfficialWhatsAppConfig | null;
  officialLoad: ConnectionLoadState;
  provider: WhatsAppProvider;
  deviceLoad: ConnectionLoadState;
  device: WhatsAppDeviceData | null;
  status: WhatsAppStatusData | null;
}): ConnectionRow[] {
  const rows: ConnectionRow[] = [];
  if (hasOfficialConnection(official) || officialLoad !== 'ready') {
    const labels: Record<string, string> = {
      provisioning: 'Ativação em andamento', disconnecting: 'Desconectando',
      disconnect_error: 'Falha ao desconectar', action_required: 'Ação necessária',
      error: 'Erro na conexão', legacy_connected: 'Conexão legada', connected: 'Conectada',
    };
    const officialStatus = officialLoad === 'loading' ? 'Verificando' : officialLoad === 'error'
      ? 'Status indisponível' : labels[official?.status || ''] || (official?.has_official ? 'Conectada' : 'Não conectada');
    rows.push({ kind: 'official', name: 'API oficial da Meta', phone: official?.display_phone_number || 'Número indisponível',
      profileName: official?.verified_name?.trim() || undefined, imageUrl: official?.profile_picture_url || undefined,
      status: officialStatus, reconnect: false,
      tone: officialLoad === 'error' || ['error', 'action_required', 'disconnect_error'].includes(official?.status || '') ? 'error'
        : officialLoad === 'ready' && officialStatus === 'Conectada' ? 'success' : 'neutral' });
  }
  if (provider || deviceLoad !== 'ready') {
    const known = deviceLoad === 'ready' && status !== null;
    const deviceStatus = deviceLoad === 'loading' ? 'Verificando' : !known ? 'Status indisponível'
      : status?.connected ? 'Conectado' : 'Desconectado';
    rows.push({ kind: 'device', provider, name: 'WhatsApp por dispositivo', phone: device?.phone || 'Número indisponível',
      profileName: device?.name?.trim() || undefined, imageUrl: device?.imgUrl || undefined,
      status: deviceStatus, reconnect: Boolean(provider && known && !status?.connected),
      tone: deviceStatus === 'Conectado' ? 'success' : deviceStatus === 'Verificando' ? 'neutral' : 'error' });
  }
  return rows;
}

export const ConnectionButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { dark: boolean; primary?: boolean }> = ({
  dark, primary, className = '', children, ...props
}) => (
  <button type="button" {...props} className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#73ACFF] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${primary ? 'bg-sminy-primary text-white hover:bg-[#0052CC]' : connectionTheme(dark).control} ${className}`}>
    {children}
  </button>
);

const ConnectionBadge: React.FC<{ row: ConnectionRow; dark: boolean }> = ({ row, dark }) => (
  <span className={`inline-flex max-w-full rounded-full px-3 py-1 text-xs font-medium ${row.tone === 'success'
    ? dark ? 'bg-[#123A2A] text-[#38C982]' : 'bg-[#ECFDF3] text-[#067647]'
    : row.tone === 'error' ? dark ? 'bg-[#3B1D24] text-[#FDA29B]' : 'bg-[#FEF3F2] text-[#B42318]'
      : dark ? 'bg-[#14243A] text-[#D0D5DD]' : 'bg-[#F5F8FF] text-[#344054]'}`}>{row.status}</span>
);

const ConnectionAvatar: React.FC<{ row: ConnectionRow; dark: boolean; large?: boolean }> = ({ row, dark, large }) => {
  const [failedImage, setFailedImage] = React.useState<string>();
  const initials = row.profileName?.split(/\s+/).slice(0, 2).map(part => Array.from(part)[0]).join('').toLocaleUpperCase('pt-BR');
  const showImage = row.imageUrl && row.imageUrl !== failedImage;
  return (
    <span className={`flex shrink-0 items-center justify-center overflow-hidden border ${large ? 'h-16 w-16 text-xl sm:h-20 sm:w-20 sm:text-2xl' : 'h-12 w-12 text-base'} ${row.kind === 'device' || showImage ? 'rounded-full' : 'rounded-2xl'} ${dark ? 'border-[#27364D] bg-[#193451] text-[#D0D5DD]' : 'border-[#D5E4F7] bg-[#EAF2FF] text-[#344054]'}`}>
      {showImage ? <img src={row.imageUrl} alt={`Foto do WhatsApp de ${row.profileName || (row.kind === 'official' ? 'conta oficial' : 'dispositivo')}`} className="h-full w-full object-cover" referrerPolicy="no-referrer" onError={() => setFailedImage(row.imageUrl)} />
        : row.kind === 'official' ? <ChannelBadge channel="official_whatsapp" variant="avatar" />
          : <span aria-hidden="true">{initials || <Smartphone className="h-6 w-6" />}</span>}
    </span>
  );
};

export const connectionFlowClass = 'w-full max-w-[760px] sm:ml-8 sm:w-[calc(100%_-_2rem)] lg:ml-16 lg:w-[calc(100%_-_4rem)]';

export const connectionPanelClass = (dark: boolean) => `w-full max-w-[824px] rounded-2xl border px-5 py-6 sm:p-8 lg:ml-8 lg:w-[calc(100%_-_2rem)] ${connectionTheme(dark).surface}`;

export const ConnectionLink: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { dark: boolean; danger?: boolean }> = ({ dark, danger, className = '', children, ...props }) => (
  <button type="button" {...props} className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md text-xs font-medium transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#73ACFF] disabled:cursor-not-allowed disabled:opacity-50 ${danger ? dark ? 'text-[#F87171]' : 'text-[#B42318]' : dark ? 'text-[#66A3FF]' : 'text-sminy-primary'} ${className}`}>{children}</button>
);

export const ConnectionDeleteButton: React.FC<{ dark: boolean; disabled?: boolean; onClick: () => void }> = ({ dark, ...props }) => (
  <ConnectionButton dark={dark} {...props} className={`border !bg-transparent !text-xs ${dark ? 'border-[#27364D] !text-[#F87171] hover:!bg-red-500/10' : 'border-[#E4E8EF] !text-[#B42318] hover:!bg-red-50'}`}>
    <Trash2 size={14} aria-hidden="true" />Excluir conexão
  </ConnectionButton>
);

export const ConnectionIdentity: React.FC<{ row: ConnectionRow; dark: boolean }> = ({ row, dark }) => (
  <div className="flex min-w-0 items-center gap-4">
    <ConnectionAvatar row={row} dark={dark} />
    <div className="min-w-0">
      <h3 className="break-words text-base font-semibold">{row.profileName || row.name}</h3>
      <p className={`mt-2 text-xs ${connectionTheme(dark).muted}`}>{row.profileName ? row.name : 'Informações do perfil indisponíveis'}</p>
    </div>
  </div>
);

export const ConnectionDetail: React.FC<{ dark: boolean; label: string; children: React.ReactNode }> = ({ dark, label, children }) => (
  <div className={`grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 border-b py-4 text-xs sm:grid-cols-[264px_minmax(0,1fr)] ${connectionTheme(dark).divider}`}>
    <dt className={connectionTheme(dark).muted}>{label}</dt><dd className="min-w-0 break-words">{children}</dd>
  </div>
);

export const DeviceConnectionProfile: React.FC<{ row: ConnectionRow; dark: boolean }> = ({ row, dark }) => (
  <div>
    <ConnectionIdentity row={row} dark={dark} />
    <dl className={`mt-8 border-t ${connectionTheme(dark).divider}`}>
      <ConnectionDetail dark={dark} label="Número">{row.phone}</ConnectionDetail>
      <ConnectionDetail dark={dark} label="Status"><span className="inline-flex items-center gap-2"><span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${row.tone === 'success' ? 'bg-emerald-500' : row.tone === 'error' ? 'bg-red-400' : 'bg-slate-400'}`} />{row.status}</span></ConnectionDetail>
    </dl>
  </div>
);

export const ConnectionList: React.FC<{
  rows: ConnectionRow[]; dark: boolean; busy: boolean; canAdd: boolean;
  onAdd: () => void; onManage: (kind: ConnectionKind) => void; onReconnect: () => void;
}> = ({ rows, dark, busy, canAdd, onAdd, onManage, onReconnect }) => {
  const t = connectionTheme(dark);
  if (!rows.length) return (
    <section className={`${connectionPanelClass(dark)} mt-8`}>
      <h2 className="text-[22px] font-semibold tracking-tight">Conecte seu primeiro WhatsApp</h2>
      <p className={`mt-4 text-[13px] leading-relaxed ${t.body}`}>Adicione o número da clínica para começar a atender pela Sminy.</p>
      <ConnectionButton dark={dark} primary disabled={busy || !canAdd} onClick={onAdd} className="mt-8 w-full !text-xs sm:w-auto">Adicionar conexão</ConnectionButton>
      <p className={`mt-5 text-xs ${t.muted}`}>API oficial da Meta ou WhatsApp por dispositivo.</p>
      <div className={`mt-12 border-t pt-6 ${t.divider}`}>
        <h3 className={`text-xs font-medium ${t.body}`}>Como funciona</h3>
        <ol className="mt-6 grid gap-5 sm:grid-cols-3">
          {['Escolha a conexão', 'Autorize o acesso', 'Confira o número'].map((text, i) => (
            <li key={text} className={`flex gap-3 text-xs ${t.body}`}><span className={t.muted}>{String(i + 1).padStart(2, '0')}</span>{text}</li>
          ))}
        </ol>
      </div>
    </section>
  );
  return (
    <section aria-label="Conexões configuradas" className="space-y-7">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className={`text-sm ${t.body}`}>{rows.some(r => r.status === 'Verificando') ? 'Verificando conexões…' : rows.some(r => r.status === 'Status indisponível') ? 'Verifique as conexões abaixo' : `${rows.length} ${rows.length === 1 ? 'conexão configurada' : 'conexões configuradas'}`}</p>
        {canAdd && <ConnectionButton dark={dark} primary onClick={onAdd} disabled={busy}><Plus size={16} aria-hidden="true" />Adicionar conexão</ConnectionButton>}
      </div>
      <div className={`overflow-hidden rounded-2xl border ${t.surface}`}>
        <table className="block w-full text-left xl:table xl:table-fixed">
          <caption className="sr-only">Tipo, número, status e ações de cada conexão do WhatsApp</caption>
          <thead className={`hidden border-b text-xs font-normal xl:table-header-group ${t.divider} ${t.muted}`}><tr>{['PERFIL / CONEXÃO', 'NÚMERO', 'STATUS', 'AÇÕES'].map((h, i) => <th key={h} scope="col" className={`px-6 py-5 font-normal ${i === 0 ? 'w-[37%]' : i === 1 ? 'w-[26%]' : 'w-[18.5%]'}`}>{h}</th>)}</tr></thead>
          <tbody className="block xl:table-row-group">
            {rows.map(row => (
              <tr key={row.kind} data-connection={row.kind} className={`block border-b last:border-b-0 xl:table-row ${t.divider}`}>
                <th scope="row" className="block px-6 pb-5 pt-6 text-sm font-semibold xl:table-cell xl:py-8">
                  <div className="flex min-w-0 items-center gap-4">
                    <ConnectionAvatar row={row} dark={dark} />
                    <div className="min-w-0"><p className="break-words text-base">{row.profileName || row.name}</p>{row.profileName && <p className={`mt-2 text-xs font-normal ${t.muted}`}>{row.name}</p>}</div>
                  </div>
                </th>
                <td className={`block break-words px-6 pb-3 text-sm xl:table-cell xl:py-8 ${t.body}`}>{row.phone}</td>
                <td className="block px-6 pb-4 xl:table-cell xl:py-8"><ConnectionBadge row={row} dark={dark} /></td>
                <td className="block px-6 pb-6 xl:table-cell xl:py-7"><ConnectionButton dark={dark} primary={row.reconnect} disabled={busy || row.status === 'Verificando'} aria-label={`${row.reconnect ? 'Reconectar' : 'Gerenciar'} ${row.name}`} onClick={() => row.reconnect ? onReconnect() : onManage(row.kind)} className="w-full xl:w-auto">{row.reconnect ? 'Reconectar' : 'Gerenciar'}</ConnectionButton></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={`text-xs leading-relaxed ${t.muted}`}>Cada conexão tem seu próprio número e pode ser gerenciada separadamente.</p>
    </section>
  );
};

export const ConnectionPicker: React.FC<{
  dark: boolean; officialAvailable: boolean; deviceAvailable: boolean;
  onSelect: (kind: ConnectionKind) => void;
}> = ({ dark, officialAvailable, deviceAvailable, onSelect }) => {
  const [selected, setSelected] = React.useState<ConnectionKind>(officialAvailable ? 'official' : 'device');
  const t = connectionTheme(dark);
  const options = [{ kind: 'official' as const, name: 'API oficial da Meta', available: officialAvailable, description: 'Autorize com sua conta Meta.' },
    { kind: 'device' as const, name: 'WhatsApp por dispositivo', available: deviceAvailable, description: 'Vincule pelo WhatsApp do celular.' }];
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight">Adicionar conexão</h2>
      <p className={`mt-3 text-[13px] ${t.body}`}>Escolha como conectar o WhatsApp da clínica.</p>
      <fieldset className={`mt-9 border-t ${t.divider}`}><legend className="sr-only">Tipo de conexão</legend>
        {options.map(option => <label key={option.kind} className={`flex min-h-[86px] cursor-pointer items-center gap-4 border-b py-5 ${t.divider} ${!option.available ? 'cursor-not-allowed opacity-60' : ''}`}>
          <input type="radio" name="connection-kind" value={option.kind} checked={selected === option.kind} disabled={!option.available} onChange={() => setSelected(option.kind)} className="h-4 w-4 shrink-0 accent-sminy-primary focus-visible:outline-offset-4" />
          <div className="min-w-0"><span className="text-sm font-semibold">{option.name}</span><p className={`mt-2 text-xs leading-relaxed ${t.muted}`}>{option.available ? option.description : 'Já configurada ou aguardando verificação. Gerencie pela lista.'}</p></div>
        </label>)}
      </fieldset>
      <p className={`mt-6 text-xs ${t.muted}`}>Cada conexão pode usar um número diferente.</p>
      <ConnectionButton dark={dark} primary disabled={selected === 'official' ? !officialAvailable : !deviceAvailable} onClick={() => onSelect(selected)} className="mt-8 w-full !text-xs sm:w-auto">{selected === 'official' ? 'Continuar com a Meta' : 'Continuar com dispositivo'}</ConnectionButton>
    </section>
  );
};

export const ConnectionBack: React.FC<{ dark: boolean; disabled?: boolean; onClick: () => void }> = ({ dark, ...props }) => <ConnectionLink dark={dark} {...props}><ArrowLeft size={14} aria-hidden="true" />Todas as conexões</ConnectionLink>;
